import React from "react";
import { currencyValue, FormatDateTimeForDB } from "../../../Utils/renderField/hookForms";
import { redondearDecimales } from "../../../../../utility/utils";

// __________ BONIFICACIÓN POR AMONIACO __________
/**
 * Calcula el bono por NH3 de una recepción.
 * @param {Object} item - Objeto que representa la recepción.
 * @returns {Object} - Objeto que contiene información sobre el bono por NH3.
 */
export const calcularBonoNH3 = (item) => {
    if (!esRecepcionLatex(item)) {
        return {
            aplicaBonoNH3: false,
            realNH3: 0,
            nh3_limite: 0,
            bono_por_kilogramo: 0,
            bono_recibido: 0,
        };
    }

    const finca = item.finca;
    const realNH3 = item.analisis_laboratorio.nh3 * 100;

    let aplicaBonoNH3 = false;
    let nh3_limite = 0;
    let bono_por_kilogramo = 0;
    let bono_recibido = 0;

    if (finca.aplica_bonif_amoniaco && finca.historial_amoniaco.length > 0) {
        aplicaBonoNH3 = true;
        const ultimoRegistro = finca.historial_amoniaco[finca.historial_amoniaco.length - 1];

        nh3_limite = ultimoRegistro.valor_limite_nh3;
        bono_por_kilogramo = ultimoRegistro.bono_por_kilogramo;

        if (realNH3 <= nh3_limite) {
            bono_recibido = redondearDecimales(parseFloat(item.kilos_secos) * bono_por_kilogramo, 4);
        }
    }

    return { aplicaBonoNH3, realNH3, nh3_limite, bono_por_kilogramo, bono_recibido };
};

/**
 * Obtiene un componente con el valor del bono por NH3.
 * @param {Object} row - Objeto que representa la recepción.
 * @param {boolean} isDollar - Indica si el valor está en dólares.
 * @param {number} cambioQuetzales - Tasa de cambio de quetzales a dólares.
 * @returns {JSX.Element} - Componente con el valor del bono por NH3.
 */
export const getBonoNH3 = (row, isDollar = true, cambioQuetzales) => {
    const { bono_recibido } = calcularBonoNH3(row);

    if (!isDollar) return currencyValue(bono_recibido * cambioQuetzales, isDollar);
    return currencyValue(bono_recibido, isDollar);
};

/**
 * Transforma una lista de recepciones para incluir información sobre el bono por NH3.
 * @param {Array} recepciones - Lista de recepciones.
 * @param {number} cambio_dolar - Tasa de cambio de dólares a quetzales.
 * @returns {Array} - Lista transformada de recepciones con información sobre el bono por NH3.
 */
export const transformarRecepcionesNH3 = (recepciones, cambio_dolar = 0) => {
    return recepciones.map((item) => {
        const {
            aplicaBonoNH3,
            nh3_limite,
            bono_por_kilogramo,
            bono_recibido,
        } = calcularBonoNH3(item);

        return {
            id_recepcion: item.movimiento.ingreso.id,
            fecha: FormatDateTimeForDB(item.movimiento.ingreso.fecha),
            // nombre: item.finca.nombre,
            id_finca: item.finca.id,
            kilos_secos: item.kilos_secos,
            nh3: item.analisis_laboratorio.nh3,
            valor_limite_nh3: nh3_limite,
            bono_por_kilogramo: bono_por_kilogramo,
            guardar: aplicaBonoNH3,
            bonificacion: bono_recibido,
            bonificacion_quetzales: redondearDecimales(bono_recibido * cambio_dolar, 4),
        };
    });
};

// __________ BONIFICACIÓN POR COSTO DE FLETE __________
/**
 * Calcula el bono por costo de flete de una recepción.
 * @param {Object} item - Objeto que representa la recepción.
 * @returns {Object} - Objeto que contiene información sobre el bono por costo de flete.
 */
export const calcularBonoFletes = (item) => {
    const finca = item.finca;

    let aplicaBonoFletes = false;
    let kg_minimos = 0;
    let bono_por_kilogramo = 0;
    let bono_recibido = 0;

    if (finca.aplica_bonif_fletes && finca.historial_bonif_fletes.length > 0) {
        aplicaBonoFletes = true;

        const ultimoRegistro = finca.historial_bonif_fletes[finca.historial_bonif_fletes.length - 1];

        kg_minimos = ultimoRegistro.bonif_fletes_min_kg;
        bono_por_kilogramo = ultimoRegistro.bonif_fletes_bono_por_kg;

        if (item.movimiento.peso_neto >= kg_minimos) {
            bono_recibido = item.movimiento.peso_neto * bono_por_kilogramo;
        }
    }

    return { aplicaBonoFletes, kg_minimos, bono_por_kilogramo, bono_recibido };
};

/**
 * Obtiene un componente con el valor del bono por costo de flete.
 * @param {*} row - Objeto que representa la recepción.
 * @param {*} isDollar - Indica si el valor está en dólares.
 * @param {*} cambioQuetzales - Tasa de cambio de quetzales a dólares.
 * @return {JSX.Element} - Componente con el valor del bono por costo de flete.
 */
export const getBonoFletes = (row, isDollar = true, cambioQuetzales) => {
    const { bono_recibido } = calcularBonoFletes(row);

    if (!isDollar) return currencyValue(bono_recibido * cambioQuetzales, isDollar);
    return currencyValue(bono_recibido, isDollar);
};

/**
 * Transforma una lista de recepciones para incluir información sobre el bono por costo de flete.
 * @param {Array} recepciones - Lista de recepciones.
 * @param {number} cambio_dolar - Tasa de cambio de dólares a quetzales.
 * @returns {Array} - Lista transformada de recepciones con información sobre el bono por costo de flete.
 */
export const transformarRecepcionesFletes = (recepciones, cambio_dolar) => {
    return recepciones.map((item) => {
        const {
            aplicaBonoFletes,
            kg_minimos,
            bono_por_kilogramo,
            bono_recibido,
        } = calcularBonoFletes(item);

        return {
            id_recepcion: item.movimiento.ingreso.id,
            fecha: FormatDateTimeForDB(item.movimiento.ingreso.fecha),
            // nombre: item.finca.nombre,
            id_finca: item.finca.id,
            kilos_secos: item.kilos_secos,
            peso_neto: item.movimiento.peso_neto,
            bonif_fletes_min_kg: kg_minimos,
            bonif_fletes_bono_por_kg: bono_por_kilogramo,
            guardar: aplicaBonoFletes,
            bonificacion: bono_recibido,
            bonificacion_quetzales: redondearDecimales(bono_recibido * cambio_dolar, 4),
        };
    });
};

export const hayRecepcionLatex = (item) => {
    return item.detalles && item.detalles.some(detalle => esRecepcionLatex(detalle));
}

export const esRecepcionLatex = (recepcion) => {
    const removeAccents = (str) => {
        return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    }

    const opcion1 = recepcion.materia_prima && removeAccents(recepcion.materia_prima.tipo_materia_prima.nombre).toLowerCase() === 'latex';
    const opcion2 = recepcion.ingreso && removeAccents(recepcion.ingreso.tipo_materia.nombre).toLowerCase() === 'latex';

    return opcion1 || opcion2;
}

export const hayRecepcionSolido = (item) => {
    return item.detalles && item.detalles.some(detalle => esRecepcionSolido(detalle));
}

export const esRecepcionSolido = (recepcion) => {
    const removeAccents = (str) => {
        return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    }

    const opcion1 = recepcion.materia_prima && removeAccents(recepcion.materia_prima.tipo_materia_prima.nombre).toLowerCase() === 'solido';
    const opcion2 = recepcion.ingreso && removeAccents(recepcion.ingreso.tipo_materia.nombre).toLowerCase() === 'solido';

    return opcion1 || opcion2;
}
